import {Box, Center, Container, Image, Spinner, Text} from "@chakra-ui/react";
import React, { memo, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {Navigate, useNavigate, useParams} from "react-router-dom";

import {getTestByPublicId, postAnswer, postTestStart, updateTimeLeft} from "../../shared/Api";
import TimerComponent from "../../shared/components/TimerComponent";
import {enableBlurProtection} from "../../shared/hooks/BlurProtection";
import {useTheme} from "../../shared/hooks/ThemeProvider";
import { CandidateStatus, Paths, QuestionParameterKey, QuestionType, SkillType } from "../../shared/model/Constants";
import { publicKeys } from "../../shared/QueryKeys";
import {
  allowOptionsShuffle,
  disableKeyCommands, disableWritingCommands, enableWritingCommands,
  extractData,
  shuffleArray
} from "../../shared/Utils";
import Question from "./Question";
import RecordingQuestion from "./RecordingQuestion";
import TestEnd from "./TestEnd";
import TestStart from "./TestStart";
import WritingQuestion from "./WritingQuestion";

const Test = () => {
  const navigate = useNavigate();
  function navigateHome() {
    navigate(Paths.HOME);
  }
  // const [started, setStarted] = useState<boolean>(true);
  const params = useParams<{
    publicId: string;
    candidateToken: string;
  }>();
  const queryClient = useQueryClient();
  const publicId = params.publicId || "";
  const candidateToken = params.candidateToken || "";

  const [oldQuestion, setOldQuestion] = useState<String>("");
  const timeLeft = useRef<number>(-1);

  useEffect(() => {
    const onBeforeUnload = (ev:any) => {
      ev.returnValue = "ret";
      return "ret";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);


  const testQuery = useQuery(publicKeys.testsByPublicId(publicId), () =>
    getTestByPublicId(publicId, candidateToken).then(extractData),
    {retry: false, refetchOnMount: true, refetchOnReconnect: false,
      onSettled: (data, error) => {
        if (data === undefined || data.id === undefined) {
          navigateHome();
        }
      },
    }
  );

  const startTestMutation = useMutation(
    ({ publicId }: any) => postTestStart(publicId, candidateToken),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(publicKeys.testsByPublicId(publicId)),
      onSettled: (data) => {
        if (data === undefined || data.status === 404) {
          navigateHome();
        }
      },
    }
  );

  const postUpdateTimeLeftMutation = useMutation(
    ({ timeLeft }: any) =>
      updateTimeLeft(publicId, candidateToken, timeLeft),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(publicKeys.testsByPublicId(publicId)),
    }
  );
  /*
   TODO: use map of answerProperties to handle optionIds, answerBlob, isValidBlob...
   */
  const postAnswerMutation = useMutation(
    ({ publicId, question, optionIds, skillType, timeLeft, answerBlob, isValidBlob }: any) =>
      postAnswer(publicId, question, optionIds, timeLeft, answerBlob, isValidBlob, candidateToken, skillType),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(publicKeys.testsByPublicId(publicId)),
      onSettled: (data) => {
        if (data === undefined || data.status === 404) {
          navigateHome();
        }
      },
    }
  );

  useEffect(() => {
    if (testQuery?.data?.skillType === SkillType.WRITING) {
      enableWritingCommands();
    } else {
      disableKeyCommands();
      enableBlurProtection();
    }
    return () => {
      if (testQuery?.data?.skillType === SkillType.WRITING) {
        disableWritingCommands();
      }
    };
  }, [testQuery]);

  if (testQuery.isError) {
    return <Navigate to={Paths.ERROR} />;
  } else if (!testQuery.data || testQuery.isFetching) {
    return (
      <Center height="200px">
        <Spinner />
      </Center>
    );
  } else {
    if (testQuery.data.status.status === CandidateStatus.INVITED) {
        timeLeft.current=-1;
        // if (started) {
        return (
            <TestStart
                test={testQuery.data}
                isLoading={startTestMutation.isLoading}
                onTestStart={() => startTestMutation.mutate({publicId})}
            />
        );
        // } else {
        //     return (<ConfirmStart skillType={testQuery.data.skillType}/>);
        // }
    } else if (
      testQuery.data.status.status === CandidateStatus.TEST_IN_PROGRESS &&
      testQuery.data.nextQuestion
    ) {
      if (timeLeft.current===-1) {
        timeLeft.current=testQuery.data.timeLeft;
      }
      //if (!postAnswerMutation.isLoading) {
      // prevent shuffle of current questions
      if (!testQuery.isLoading && (oldQuestion !== testQuery.data.nextQuestion.statement) &&
           allowOptionsShuffle(testQuery.data.nextQuestion)) {
        testQuery.data.nextQuestion.options = shuffleArray(testQuery.data.nextQuestion.options);
        setOldQuestion(testQuery.data.nextQuestion.statement);
      }
      return <>
      { testQuery.data.timeLimit !== 0 &&
              <TimerComponent timeLeft={timeLeft}
                              onTimerFinished={(timeLeftP: number) => {
                                // only ends test if it is single adaptive, not multistage
                                //if (props.test.testType === TestType.ADAPTIVE &&
                                //  (props.test.conditions && props.test.conditions.includes("ADAPTIVE"))
                                //)
                                  postUpdateTimeLeftMutation.mutate({ timeLeft:timeLeft.current });
                                }
                              }
                          offsetDisplay={true}
                        />
      }

      {testQuery.data.nextQuestion.questionType===QuestionType.MULTIPLE_CHOICE ?
                    <Question
                         test={testQuery.data}
                         status={testQuery.data.status}
                         isLoading={postAnswerMutation.isLoading}
                         onNextQuestion={(optionIds: string[], timeLeftP: number) =>
                           postAnswerMutation.mutate({
                             publicId,
                             question: testQuery.data.nextQuestion,
                             optionIds,
                             skillType: testQuery.data.skillType,
                             timeLeft: timeLeft.current,
                             answerBlob: undefined,
                             isValidBlob: false
                           })
                         }
                       />
                :  testQuery.data.nextQuestion.questionType===QuestionType.COMPOSITION &&
                   testQuery.data.skillType===SkillType.SPEAKING ?
                    <RecordingQuestion
                      test={testQuery.data}
                      status={testQuery.data.status}
                      isLoading={postAnswerMutation.isLoading}
                      preparationTime={(+testQuery.data.nextQuestion.parameters[QuestionParameterKey.PREPARATION_TIME])}
                      responseTime={(+testQuery.data.nextQuestion.parameters[QuestionParameterKey.RESPONSE_TIME])}
                      onNextQuestion={(timeLeftP: number) => {
                                                postUpdateTimeLeftMutation.mutate({ timeLeft: timeLeft.current });
                        }
                      }
                    />
                    : testQuery.data.nextQuestion.questionType===QuestionType.COMPOSITION &&
                                         testQuery.data.skillType===SkillType.WRITING ?
                    <WritingQuestion
                      test={testQuery.data}
                      status={testQuery.data.status}
                      isLoading={postAnswerMutation.isLoading}
                      onNextQuestion={(timeLeftP: number, answerText : string) => {
                           postAnswerMutation.mutate({
                             publicId,
                             question: testQuery.data.nextQuestion,
                             optionIds:[],
                             skillType: testQuery.data.skillType,
                             timeLeft: timeLeft.current,
                             answerBlob: answerText,
                             isValidBlob: true
                           })
                         }
                      }
                    />

                    :
                    <>
                    </>
      }

      </>
    } else if (testQuery.data.status.status === CandidateStatus.TEST_COMPLETE) {
      timeLeft.current=-1;
      return <TestEnd testName={testQuery.data.name} skillType={testQuery.data.skillType}
      timerStopped={testQuery.data.timeLimit > 0 && testQuery.data.timeLeft === 0}
                       />;
    } else {
      return <Text>La prueba solicitada no existe</Text>;
    }
  }
};

const RenderTest = () => {
  const theme = useTheme().theme;
  return (
    <Container maxW="container.lg" marginX="auto"
          filter= {'grayscale('+theme.IMG_GRAYSCALE+') contrast('+theme.IMG_CONTRAST+')'}>
      <Box
          bgImage="'/images/img-1e0eab8174642bd1921638f9bd71e94648399338.png'"
          bgSize= "cover" bgRepeat= "no-repeat"
          minHeight="610px"  width="100%" height="auto"
          borderWidth='1px' borderRadius='lg'>

        <Box paddingTop={5} />
        <Test/>
        <Image
          src="/images/51b7-15d0-10a4-f7ae.png"
          maxH="6rem" bottom="16px" />
      </Box>
    </Container>
  )
}
// Image position="absolute"
export default memo(RenderTest);